import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";
import { usePartnerAddresses, useSyncStatus } from "@stockline/web";
import {
  NO_MATCH_PARTNER_ID,
  v2PartnerAddressToLegacy,
} from "@/query/hooks/usePartners";
import type { PartnerAddress } from "@/app/api/types/partner";
import {
  DropdownChevron,
  DropdownTrigger,
  SearchableDropdownContent,
  type SearchableDropdownItem,
} from "./dropdown-primitives";
import { usePortalDropdown } from "./dropdown-hooks";
import { formatAddressLabel } from "../../utils/addressFormat";

interface ShippingAddressSelectorCellProps {
  readonly partnerId: string | null;
  readonly value: string | null;
  readonly onChange: (addressId: string | null) => void;
  /**
   * Embedded `partner.addresses` from the legacy partner payload — used as a
   * cold-start fallback before the V2 Dexie pull completes.
   */
  readonly fallbackAddresses?: PartnerAddress[];
}

/**
 * Shipping-address dropdown for the pre-order header. Mirrors `LocationButton`
 * — same `DropdownTrigger` + `SearchableDropdownContent` primitives, same
 * compact look — so the three header pickers (address / location / route)
 * sit next to each other without visual noise.
 *
 * Owns its own Dexie subscription via `usePartnerAddresses` so PreOrderControls
 * doesn't need a partner reference; falls back to the partner payload's
 * embedded addresses until the offline-first store hydrates. Hides itself
 * when there's no partner or no addresses on file.
 */
export function ShippingAddressSelectorCell({
  partnerId,
  value,
  onChange,
  fallbackAddresses,
}: ShippingAddressSelectorCellProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  // Sentinel UUID when partner is unset so the live query never returns the
  // unfiltered cross-partner list (same guard the legacy slot used).
  const v2Addresses = usePartnerAddresses({
    partnerId: partnerId ?? NO_MATCH_PARTNER_ID,
  });
  const addressesSyncStatus = useSyncStatus("partner-addresses");

  const allAddresses = useMemo<PartnerAddress[]>(() => {
    if (!partnerId) return [];
    const live = v2Addresses.map(v2PartnerAddressToLegacy);
    if (addressesSyncStatus === "ready") return live;
    return live.length > 0 ? live : (fallbackAddresses ?? []);
  }, [partnerId, v2Addresses, addressesSyncStatus, fallbackAddresses]);

  // Ship-to choices exclude the billing-party row (`is_billing`, STK-458).
  // Data-driven: tenants with no flagged rows see the full list unchanged.
  const addresses = useMemo(
    () => allAddresses.filter((a) => !a.is_billing),
    [allAddresses],
  );

  // Selected lookup stays on the FULL list: an order already bound to the
  // billing row must render its value, not a blank trigger.
  const selected = useMemo(
    () => allAddresses.find((a) => a.id === value) ?? null,
    [allAddresses, value],
  );

  const noneLabel = t("ordersRecap.noShippingAddress", "No shipping address");
  const placeholder = t("ordersRecap.shippingAddress", "Shipping address");
  const displayLabel = selected ? formatAddressLabel(selected) : placeholder;

  // Build items: "no shipping address" first (acts as the clear-selection
  // action), then matching addresses with their city as a sublabel.
  const items = useMemo<SearchableDropdownItem[]>(() => {
    const needle = search.toLowerCase().trim();
    const list: SearchableDropdownItem[] = [{ id: null, label: noneLabel }];
    for (const addr of addresses) {
      const label = formatAddressLabel(addr);
      if (needle && !label.toLowerCase().includes(needle)) continue;
      const cityLine = [addr.zip_code, addr.city]
        .filter((p) => p && p.trim().length > 0)
        .join(" ");
      list.push({
        id: addr.id,
        label,
        sublabel: cityLine || undefined,
      });
    }
    return list;
  }, [addresses, noneLabel, search]);

  const setOpenRef = useRef<(v: boolean) => void>(() => {});

  const handleSelectByIndex = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) return;
      onChange(item.id);
      setOpenRef.current(false);
    },
    [items, onChange],
  );

  const resetSearch = useCallback(() => setSearch(""), []);

  const dd = usePortalDropdown({
    itemCount: items.length,
    onSelect: handleSelectByIndex,
    width: 240,
    resetOnClose: [resetSearch],
  });
  setOpenRef.current = dd.setOpen;

  // Hide entirely when there's no partner or no addresses to pick from —
  // matches the legacy selector's contract so the header doesn't show a
  // dead dropdown.
  if (!partnerId || addresses.length === 0) return null;

  return (
    <>
      <DropdownTrigger
        ref={dd.triggerRef}
        open={dd.open}
        onClick={(e) => {
          e.stopPropagation();
          dd.setOpen(!dd.open);
        }}
        data-testid="shipping-address-button"
      >
        <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
        <span className="truncate">{displayLabel}</span>
        <DropdownChevron open={dd.open} className="ml-auto" />
      </DropdownTrigger>

      <SearchableDropdownContent
        dd={dd}
        searchQuery={search}
        onSearchChange={setSearch}
        items={items}
        value={value}
        onChange={onChange}
        emptyMessage={t(
          "ordersRecap.noShippingAddressFound",
          "No shipping address found.",
        )}
        searchPlaceholder={t(
          "ordersRecap.searchShippingAddress",
          "Search shipping address...",
        )}
        hasSearchIcon={false}
        maxHeight="200px"
      />
    </>
  );
}
